import { Dispatch, SetStateAction } from 'react'
import axios from 'lib/axios'
import toast from 'react-hot-toast'
import { HotToast } from 'ui/components/toast'
import {
  CPILibraryItem,
  CPIManifest,
  CPIManifestUIFormulaParam,
  CPIManifestUIFormulaParamSection,
  IFramework,
  IntegrationInstance
} from 'models'

export const getFormValues = (values: any) => {
  const formValues = {
    ...values
  }
  for (const [key, value] of Object.entries(values)) {
    if (key !== 'cpiCategoryValue') {
      formValues[key] = Number(value)
    }
  }
  delete formValues.cpiCategoryValue

  return formValues
}

interface SubmitCustomFwCpiProps {
  active: boolean
  fw: string
  customFramework: IFramework | null
  values: any
  manifestData: CPIManifest
}

export const handleCustomFwCpi = async ({
  active,
  fw,
  customFramework,
  values,
  manifestData
}: SubmitCustomFwCpiProps) => {
  if (!active && fw === 'custom' && customFramework) {
    const cat = values.cpiCategoryValue

    try {
      const allCategoriesRes = await axios.get(`/api/v3/frameworks/categories?framework=${customFramework.id}`)

      if (allCategoriesRes.status.toString().includes('20')) {
        const allCategories = allCategoriesRes.data.data.categories

        const found = allCategories.find((n: any) => n.title === cat)

        if (found) {
          const categoryId = found.id

          const cpisRes = await axios.get(
            `/api/v3/frameworks/cpis/${categoryId}/weights?framework=${customFramework.id}`
          )

          if (cpisRes.status.toString().includes('20')) {
            const cpisData = cpisRes.data.data
            const { cpis } = cpisData
            const cpisWeight = cpis.reduce((acc: any, obj: any) => (acc + !isNaN(obj.weight) ? obj.weight : 0), 0)
            const newList = [
              ...cpis,
              {
                name: manifestData.name,
                displayName: manifestData.display_name,
                title: manifestData.title,
                isActive: true,
                weight: cpis.some((n: any) => n.weight && n.weight === 100)
                  ? 0
                  : cpisWeight < 100
                  ? 100 - cpisWeight
                  : 0
              }
            ]

            await axios.post(`/api/v3/frameworks/custom/${categoryId}/cpis`, {
              cpis: newList
            })
          }
        }
      }
    } catch (err) {
      console.error(err)
      const message = active ? 'Failed updating CPI' : 'Failed activating CPI.'
      toast.error(() => <HotToast text="Error" description={message} />)
    }
  }
}

interface GetCheckedIntegrationsNamesProps {
  checked: Record<string, boolean>
}

export const getCheckedIntegrationsNames = ({ checked }: GetCheckedIntegrationsNamesProps): string[] => {
  const checkedIntegrationsNames: string[] = []

  for (const [key, value] of Object.entries(checked)) {
    if (value) {
      checkedIntegrationsNames.push(key)
    }
  }

  return checkedIntegrationsNames
}

interface GetIntegrationIdsProps {
  checkedIntegrationsNames: string[]
  cpiSupportedAndUserConfiguredList: IntegrationInstance[]
}

export const getIntegrationIds = ({
  checkedIntegrationsNames,
  cpiSupportedAndUserConfiguredList
}: GetIntegrationIdsProps): string[] => {
  const selectedIntegrations =
    checkedIntegrationsNames.length > 0
      ? cpiSupportedAndUserConfiguredList.map((configured) =>
          configured.is_v2
            ? checkedIntegrationsNames.includes(configured.instance_name || '')
              ? configured.instance_name || ''
              : ''
            : checkedIntegrationsNames.includes(configured.name)
            ? configured.id
            : ''
        )
      : ['']

  const integrationIds =
    selectedIntegrations.length > 0 ? selectedIntegrations.filter((n) => n !== '') : selectedIntegrations

  return integrationIds
}

interface GetSelectedIntegrationsProps {
  checked: Record<string, boolean>
  userConfiguredIntegrations: IntegrationInstance[]
  setIsUpdating: Dispatch<SetStateAction<boolean>>
}

export const getSelectedIntegrations = ({
  checked,
  userConfiguredIntegrations,
  setIsUpdating
}: GetSelectedIntegrationsProps) => {
  const checkedIntegrationsNames = getCheckedIntegrationsNames({ checked })

  const selectedIntegrations =
    checkedIntegrationsNames.length > 0
      ? userConfiguredIntegrations
          .map((configured: IntegrationInstance) => {
            const nameToCheck = configured.is_v2 ? configured.instance_name : configured.name
            return nameToCheck && checkedIntegrationsNames.includes(nameToCheck) ? configured.id : ''
          })
          .filter((c) => !!c)
      : ['']

  if (selectedIntegrations.length < 0 || (selectedIntegrations.length === 1 && selectedIntegrations[0] === '')) {
    toast.error(() => <HotToast text="Error" description="Please select at least 1 data source" />)
    setIsUpdating(false)

    return []
  }

  return selectedIntegrations
}

interface GetManifestUserInputProps {
  manifestData: CPIManifest
  values: any
  cpi: CPILibraryItem
}

export const getManifestUserInput = ({ manifestData, values, cpi }: GetManifestUserInputProps) => {
  const formulaParams: Array<Pick<CPIManifestUIFormulaParam, 'name' | 'sections' | 'value'>> = []
  const formulaSections: CPIManifestUIFormulaParamSection[] = []
  const formValues = getFormValues(values)

  const { visualization_param: visualization, formula_params: formula } = manifestData.ui.trend_chart.user_input

  if ((formula.length > 0 && formula[0].sections.length > 0) || visualization.sections.length > 1) {
    for (const [key, value] of Object.entries(formValues)) {
      /* cpi036 workaround */
      if (key !== 'sla' && key !== 'issues_threshold') {
        const result: CPIManifestUIFormulaParamSection = {
          name: key,
          value: Number(value)
        }
        formulaSections.push(result)
      }
    }
  }

  if (formula && formula.length > 0) {
    formula.forEach((n) => {
      const result: Pick<CPIManifestUIFormulaParam, 'name' | 'sections' | 'value'> = {
        name: n.name,
        sections: formulaSections
      }

      if (n.value) {
        result.value = formValues[n.name]
      }

      formulaParams.push(result)
    })
  }

  const manifestUserInput = {
    formula_params: formulaParams.length > 0 ? formulaParams : undefined,
    visualization_param: {
      identical_section_values: visualization.identical_section_values,
      identical_value: visualization.identical_section_values ? formValues.sla : undefined,
      sections: !visualization.identical_section_values ? formulaSections : undefined
    }
  }

  if (!manifestUserInput.formula_params) {
    delete manifestUserInput.formula_params
  }

  const whiteList = [
    'Training & Awareness',
    'Cloud Security',
    'Identity & Access Management',
    'Detection & Response',
    'Threat Detection'
  ]
  const allowZeroSLA = cpi.name === 'cpi997' || whiteList.includes(manifestData.categories[0])
  if ([null, undefined].includes(manifestUserInput.visualization_param.identical_value) && !allowZeroSLA) {
    delete manifestUserInput.visualization_param.identical_value
  }

  if (
    !manifestUserInput.visualization_param.sections ||
    manifestUserInput.visualization_param.identical_section_values
  ) {
    delete manifestUserInput.visualization_param.sections
  }

  return manifestUserInput
}

interface UpdateCriticalSlaProps {
  data: any
  manifestData: CPIManifest
  setCriticalSla: Dispatch<SetStateAction<number | undefined>>
}

export const updateCriticalSla = ({ data, manifestData, setCriticalSla }: UpdateCriticalSlaProps) => {
  if (!data) return

  const visualizationParam = manifestData.ui.trend_chart.user_input.visualization_param

  if (!visualizationParam) return

  if (visualizationParam.sections && visualizationParam.sections.length > 0) {
    const defaultSection = manifestData.ui.trend_chart.user_input.visualization_param?.default_section
    const foundItem = visualizationParam.sections.find((item: { name: string }) => item.name === defaultSection)

    setCriticalSla(foundItem?.value ?? visualizationParam.sections[1]?.value)
  } else if (visualizationParam.identical_section_values) {
    setCriticalSla(visualizationParam.identical_value)
  }
}

interface GetManifestUserInputSnowflakeProps {
  userInputSnowflake: any
  formValues: any
}

export const getManifestUserInputSnowflake = ({
  userInputSnowflake,
  formValues
}: GetManifestUserInputSnowflakeProps) => {
  const formulaParamsSnowflake: Array<Pick<CPIManifestUIFormulaParam, 'name' | 'sections' | 'value'>> = []
  const formulaSectionsSnowflake: CPIManifestUIFormulaParamSection[] = []

  if (
    (userInputSnowflake.formula_params.length > 0 && userInputSnowflake.formula_params[0].sections.length > 0) ||
    userInputSnowflake.visualization_param.sections.length > 1
  ) {
    for (const [key, value] of Object.entries(formValues)) {
      if (key !== 'sla' && key !== 'issues_threshold') {
        const result: CPIManifestUIFormulaParamSection = {
          name: key,
          value: Number(value)
        }
        formulaSectionsSnowflake.push(result)
      }
    }
  }

  if (userInputSnowflake.formula_params && userInputSnowflake.formula_params.length > 0) {
    userInputSnowflake.formula_params.forEach((n: any) => {
      const result: Pick<CPIManifestUIFormulaParam, 'name' | 'sections' | 'value'> = {
        name: n.name,
        sections: formulaSectionsSnowflake
      }

      if (n.value) {
        result.value = formValues[n.name]
      }

      formulaParamsSnowflake.push(result)
    })
  }

  const manifestUserInputSnowflake = {
    formula_params: formulaParamsSnowflake.length > 0 ? formulaParamsSnowflake : [],
    visualization_param: {
      ...userInputSnowflake.visualization_param,
      identical_section_values: userInputSnowflake.visualization_param.identical_section_values,
      identical_value: userInputSnowflake.visualization_param.identical_section_values ? formValues.sla : undefined,
      sections: !userInputSnowflake.visualization_param.identical_section_values ? formulaSectionsSnowflake : undefined
    }
  }

  if (
    !manifestUserInputSnowflake.visualization_param.sections ||
    manifestUserInputSnowflake.visualization_param.identical_section_values
  ) {
    delete manifestUserInputSnowflake.visualization_param.sections
  }

  return manifestUserInputSnowflake
}
